import { useEffect, useRef, useState } from "react";
import { Cue, play } from "./sound";
import { palette, TIMER_URGENT_FRACTION } from "./tokens";

/**
 * Draining bar for every timed phase, with the exact time left centred inside
 * it. plan.md §2.4 originally specified urgency *without* a number; the number
 * was added deliberately — see the deviation recorded in §5. A six-minute setup
 * window is not something anyone can read off a bar.
 *
 * Driven by an absolute deadline rather than a countdown, so a backgrounded
 * tab or a paused timer cannot drift away from the server's view of when the
 * window closes.
 *
 * The fill drains by clipping a full-width layer rather than by shrinking a
 * narrow one. Both layers therefore centre the same text at the same place, and
 * the clip alone decides how much of the dark copy is showing — so the number
 * stays put on the bar while the fill passes underneath it, dark where it has
 * been reached and cream where it has not. Centring inside a shrinking element
 * would instead drag the number leftwards as time ran out.
 */

/** `M:SS`, the same shape for a thirty-second turn and a six-minute setup. */
const clock = (ms: number): string => {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, "0")}`;
};
export const TimerBar = ({
  deadlineAt,
  durationMs,
}: {
  deadlineAt: number;
  durationMs: number;
}) => {
  const [remaining, setRemaining] = useState(() =>
    Math.max(deadlineAt - Date.now(), 0),
  );

  useEffect(() => {
    // 100ms is smooth enough at bar resolution and far cheaper than rAF for
    // something that only ever moves in one direction.
    const id = setInterval(
      () => setRemaining(Math.max(deadlineAt - Date.now(), 0)),
      100,
    );
    return () => clearInterval(id);
  }, [deadlineAt]);

  const fraction = durationMs > 0 ? remaining / durationMs : 0;
  const urgent = fraction <= TIMER_URGENT_FRACTION;

  // One dry tick for each displayed second from 10 through 1. Tracking the
  // integer prevents the 100ms visual refresh from playing it ten times.
  const tickedSecond = useRef<number | null>(null);
  useEffect(() => {
    tickedSecond.current = null;
  }, [deadlineAt]);

  const seconds = Math.ceil(remaining / 1000);
  useEffect(() => {
    if (seconds < 1 || seconds > 10 || tickedSecond.current === seconds) return;
    tickedSecond.current = seconds;
    play(Cue.TimeLow);
  }, [seconds]);

  const clipped = (1 - Math.min(Math.max(fraction, 0), 1)) * 100;
  const label = clock(remaining);

  return (
    <div
      className="relative h-5 w-full overflow-hidden bg-surface"
      role="timer"
      aria-label={`${seconds} seconds remaining`}
    >
      {/* Cream on the empty track. */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="text-xs font-bold tabular-nums tracking-widest"
          style={{ color: palette.ink }}
        >
          {label}
        </span>
      </div>

      {/* The fill, carrying its own copy of the number in the ink that reads on
          it — dark on gold, cream on rust. Clipped to what is left. */}
      <div
        aria-hidden="true"
        className="transition-timer-clip absolute inset-0 flex items-center justify-center"
        style={{
          backgroundColor: urgent ? palette.sideB : palette.sideA,
          clipPath: `inset(0 ${clipped}% 0 0)`,
        }}
      >
        <span
          className="text-xs font-bold tabular-nums tracking-widest"
          style={{ color: urgent ? palette.ink : palette.bg }}
        >
          {label}
        </span>
      </div>
    </div>
  );
};
